import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Bookmark, BookmarkCheck, Share2 } from "lucide-react";
import { toast } from "sonner";
import { cachedGet, favorites, history } from "@/lib/publicApi";
import { EmergencyBar, Skeleton } from "./SaudeLayout";
import { StateBlock } from "@/components/common";
import { Markdown } from "@/components/Markdown";

const BLOCKS = [
  ["what_is", "O que é"],
  ["signs", "Sinais que podem indicar necessidade de ajuda"],
  ["impacts", "Impactos possíveis na vida da pessoa e da rede de apoio"],
  ["when_to_seek", "Quando procurar avaliação profissional"],
  ["how_assessment_works", "Como funciona a avaliação da Erimus"],
  ["treatment_options", "Formas de tratamento oferecidas"],
  ["faq", "Perguntas frequentes"],
];

export const TreatmentList = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await cachedGet("/treatments");
        setItems(data);
      } catch {
        setItems([]);
      }
    })();
  }, []);

  if (!items) return <Skeleton lines={5} testId="treatments-skeleton" />;

  return (
    <div data-testid="saude-treatments-page" className="space-y-6">
      <div>
        <p className="er-label">Tratamentos</p>
        <h1 className="mt-3 font-display text-3xl font-medium tracking-tight text-ink-900">O que a Erimus acompanha</h1>
        <p className="mt-3 text-sm leading-relaxed text-ink-600">
          Cada tratamento explica o que é, sinais de atenção, quando buscar avaliação e como o cuidado acontece.
        </p>
      </div>

      {items.length === 0 ? (
        <StateBlock testId="treatments-empty" title="Conteúdo indisponível no momento" description="Tente novamente quando tiver conexão." />
      ) : (
        <div className="space-y-3">
          {items.map((t) => (
            <Link
              key={t.id}
              to={`/saude/tratamentos/${t.slug}`}
              data-testid={`treatment-card-${t.slug}`}
              className="er-card flex items-start justify-between gap-3"
            >
              <span>
                <span className="block font-display text-base font-medium text-ink-900">{t.title}</span>
                <span className="mt-1 block text-sm text-ink-500">{t.summary}</span>
              </span>
              <ArrowRight size={16} className="mt-1 shrink-0 text-sage-600" />
            </Link>
          ))}
        </div>
      )}
      <EmergencyBar />
    </div>
  );
};

export const TreatmentDetail = () => {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data: payload } = await cachedGet(`/contents/${slug}`);
        setData(payload);
        setSaved(favorites.has(slug));
        history.push({ slug, title: payload.content.title, type: payload.content.type });
      } catch {
        setError("Não foi possível carregar este tratamento agora.");
      }
    })();
  }, [slug]);

  if (error) return <StateBlock testId="treatment-error" title="Conteúdo indisponível" description={error} />;
  if (!data) return <Skeleton lines={6} testId="treatment-skeleton" />;

  const c = data.content;

  const share = async () => {
    const text = `${c.title} — Erimus`;
    try {
      if (navigator.share) await navigator.share({ title: c.title, text });
      else {
        await navigator.clipboard.writeText(`${text} · ${window.location.href}`);
        toast.success("Link copiado");
      }
    } catch {
      /* usuário cancelou */
    }
  };

  return (
    <div data-testid="treatment-detail-page" className="space-y-7">
      <Link to="/saude/tratamentos" data-testid="treatment-back" className="inline-flex items-center gap-2 text-sm text-ink-500">
        <ArrowLeft size={16} /> Tratamentos
      </Link>

      <div>
        <p className="er-label">Tratamento</p>
        <h1 className="mt-3 font-display text-3xl font-medium tracking-tight text-ink-900">{c.title}</h1>
        <p className="mt-3 text-sm leading-relaxed text-ink-600">{c.summary}</p>
        {c.body ? <Markdown testId="treatment-body" className="mt-2">{c.body}</Markdown> : null}
      </div>

      <div className="flex gap-3">
        <button
          data-testid="treatment-save"
          onClick={() => setSaved(favorites.toggle({ slug, title: c.title, type: c.type }))}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-border bg-white px-5 text-sm text-ink-700"
        >
          {saved ? <BookmarkCheck size={16} className="text-sage-600" /> : <Bookmark size={16} />}
          {saved ? "Salvo" : "Salvar"}
        </button>
        <button
          data-testid="treatment-share"
          onClick={share}
          className="inline-flex min-h-[44px] items-center gap-2 rounded-full border border-border bg-white px-5 text-sm text-ink-700"
        >
          <Share2 size={16} /> Compartilhar
        </button>
      </div>

      {BLOCKS.map(([key, label]) =>
        c.blocks?.[key] ? (
          <section key={key} data-testid={`treatment-block-${key}`} className="er-card">
            <p className="er-label">{label}</p>
            <Markdown testId={`treatment-block-body-${key}`}>{c.blocks[key]}</Markdown>
          </section>
        ) : null
      )}

      {data.related?.length ? (
        <section data-testid="treatment-related">
          <h2 className="font-display text-lg font-medium text-ink-900">Conteúdos relacionados</h2>
          <div className="mt-3 space-y-3">
            {data.related.map((r) => (
              <Link key={r.id} to={`/saude/conteudos/${r.slug}`} className="er-card block" data-testid={`treatment-related-${r.slug}`}>
                <span className="font-display text-base font-medium text-ink-900">{r.title}</span>
                <span className="mt-1 block text-sm text-ink-500">{r.summary}</span>
              </Link>
            ))}
          </div>
        </section>
      ) : null}

      <section className="er-card" data-testid="treatment-editorial">
        <p className="er-label">Revisão editorial</p>
        <p className="mt-2 text-xs text-ink-500">
          Última revisão clínica: {c.reviewed_at ? new Date(c.reviewed_at).toLocaleDateString("pt-BR") : "pendente"} ·
          Responsável: {c.reviewer || "a definir pela equipe Erimus"}
        </p>
        <p className="mt-2 text-xs text-ink-400">{c.references}</p>
      </section>

      <div className="space-y-3">
        <Link
          to="/saude/fale-com-a-erimus/avaliacao"
          data-testid="treatment-cta-evaluation"
          className="flex min-h-[52px] items-center justify-center rounded-full bg-sage-600 px-6 text-sm font-medium text-bone"
        >
          Quero solicitar uma avaliação
        </Link>
        <Link
          to="/saude/fale-com-a-erimus/pergunta"
          data-testid="treatment-cta-question"
          className="flex min-h-[52px] items-center justify-center rounded-full border border-sage-600/40 bg-white px-6 text-sm font-medium text-sage-700"
        >
          Tenho uma dúvida anônima
        </Link>
      </div>
      <EmergencyBar />
    </div>
  );
};
